import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  AcademicCapIcon,
  UsersIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { 

  useGetStudentsQuery,
  useAddStudentMutation,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useGetConsultantsQuery
} from '../store/api';
import Pagination from '../components/Pagination';
import ExportButton from '../components/ExportButton';
import { usePermission } from '../hooks/useAuth';
import PermissionButton from '../components/PermissionButton';

export default function Students() {
  // State hooks
  const [searchTerm, setSearchTerm] = useState('');
  const [consultantFilter, setConsultantFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [debouncedConsultantFilter, setDebouncedConsultantFilter] = useState('');
  const [debouncedActiveFilter, setDebouncedActiveFilter] = useState('');

  // Use the new permission system with correct permission names - strictly permission-based
  const canAddStudent = usePermission("student.add");
  const canEditStudent = usePermission("student.edit");
  const canDeleteStudent = usePermission("student.delete");
  const canViewStudent = usePermission("student.view");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce consultant filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConsultantFilter(consultantFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [consultantFilter]);

  // Debounce active filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedActiveFilter(activeFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFilter]);

  // API hooks
  const { data: studentsResponse, error: studentsError, isLoading: studentsLoading } = useGetStudentsQuery({
    keyword: debouncedSearchTerm || undefined,
    consultant_id: debouncedConsultantFilter || undefined,
    is_active: debouncedActiveFilter || undefined
  });
  const { data: consultantsResponse, error: consultantsError } = useGetConsultantsQuery();
  const [addStudent] = useAddStudentMutation();
  const [updateStudent] = useUpdateStudentMutation();  
  const [deleteStudent] = useDeleteStudentMutation();
  
  // Laravel pagination format: data.data contains the actual students array  
  const students = Array.isArray(studentsResponse?.data?.data) ? studentsResponse.data.data : 
                   Array.isArray(studentsResponse?.data) ? studentsResponse.data : 
                   Array.isArray(studentsResponse) ? studentsResponse : [];
                   
  const consultants = Array.isArray(consultantsResponse?.data) ? consultantsResponse.data : 
                      Array.isArray(consultantsResponse) ? consultantsResponse : [];
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isExport, setIsExport] = useState(0);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    birth_date: '',
    address: '',
    parent_name: '',
    parent_phone: '',
    consultant_id: '',
    education_level: '',
    specialization: '',
    current_year: '',
    current_university: '',
    language_exam: '',
    language_exam_date: '',
    other_exams: '',
    gpa: '',
    is_active: true
  });

  // Backend filtering is now handled in API, so we use students directly
  const filteredStudents = students;

  // Pagination calculations
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // Export data preparation
  const exportData = filteredStudents.map(student => ({
    'Ad': student.first_name || student.firstName,
    'Soyad': student.last_name || student.lastName,
    'Telefon': student.phone || '',
    'Email': student.email || '',
    'Doğum Tarixi': student.birth_date || student.birthDate || '',
    'Ünvan': student.address || '',
    'Valideyn Adı': student.parent_name || student.parentName || '',
    'Valideyn Telefonu': student.parent_phone || student.parentPhone || '',
            'Konsultant': student.consultant?.admin?.first_name ? 
          `${student.consultant.admin.first_name} ${student.consultant.admin.last_name}` : 
          student.consultant?.name || 
          (typeof student.consultant === 'string' ? student.consultant : '') || '',
          'Təhsil Səviyyəsi': student.education_level?.name || student.educationLevel?.name || (typeof student.education_level === 'string' ? student.education_level : '') || (typeof student.educationLevel === 'string' ? student.educationLevel : '') || '',
            'İxtisaslaşma': student.specialization?.name || (typeof student.specialization === 'string' ? student.specialization : '') || '',
    'Cari İl': student.current_year || student.currentYear || '',
    'Cari Universitet': student.current_university || student.currentUniversity || '',
    'Dil İmtahanı': student.language_exam || student.languageExam || '',
    'Dil İmtahan Tarixi': student.language_exam_date || student.languageExamDate || '',
    'Digər İmtahanlar': student.other_exams || student.otherExams || '',
    'GPA': student.gpa || '',
    'Status': (student.is_active !== undefined ? student.is_active : student.isActive) ? 'Aktiv' : 'Deaktiv',
    'ID': student.id
  }));

  const exportHeaders = [
    'Ad', 'Soyad', 'Telefon', 'Email', 'Doğum Tarixi', 'Ünvan', 'Valideyn Adı', 'Valideyn Telefonu',
    'Mənbə', 'Konsultant', 'Təhsil Səviyyəsi', 'İxtisaslaşma', 'Cari İl', 'Cari Universitet',
    'Maraq Dairəsi Ölkələr', 'Maliyyə Proqnozu', 'Təhsil Haqqı Aralığı', 'Dil İmtahanı',
    'Dil İmtahan Tarixi', 'Digər İmtahanlar', 'GPA', 'Qeydlər', 'Status', 'ID'
  ];

  const handleAdd = () => {
    setEditingStudent(null);
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      birth_date: '',
      address: '',
      parent_name: '',
      parent_phone: '',
      consultant_id: '',
      education_level: '',
      specialization: '',
      current_year: '',
      current_university: '',
      language_exam: '',
      language_exam_date: '',
      other_exams: '',
      gpa: '',
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name || student.firstName || '',
      last_name: student.last_name || student.lastName || '',
      phone: student.phone || '',
      email: student.email || '',
      birth_date: (() => {
        const date = student.birth_date || student.birthDate;
        if (!date) return '';
        try {
          return date.includes('T') ? date.split('T')[0] : date;
        } catch {
          return date;
        }
      })(),
      address: student.address || '',
      parent_name: student.parent_name || student.parentName || '',
      parent_phone: student.parent_phone || student.parentPhone || '',
      consultant_id: student.consultant_id ? student.consultant_id.toString() : (student.consultantId ? student.consultantId.toString() : ''),
      education_level: student.education_level?.name || student.educationLevel?.name || (typeof student.education_level === 'string' ? student.education_level : '') || (typeof student.educationLevel === 'string' ? student.educationLevel : '') || '',
      specialization: student.specialization?.name || (typeof student.specialization === 'string' ? student.specialization : '') || '',
      current_year: student.current_year || student.currentYear || '',
      current_university: student.current_university || student.currentUniversity || '',
      language_exam: student.language_exam || student.languageExam || '',
      language_exam_date: (() => {
        const date = student.language_exam_date || student.languageExamDate;
        if (!date) return '';
        try {
          return date.includes('T') ? date.split('T')[0] : date;
        } catch {
          return date;
        }
      })(),
      other_exams: student.other_exams || student.otherExams || '',
      gpa: student.gpa || '',
      is_active: student.is_active !== undefined ? student.is_active : (student.isActive !== undefined ? student.isActive : true)
    });
    setIsModalOpen(true);
  };

  const handleView = (student) => {
    setViewingStudent(student);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handleDelete = async (studentId, studentName) => {
    if (window.confirm(`"${studentName}" tələbəsini silmək istədiyinizə əminsiniz?`)) {
      try {
        await deleteStudent(studentId).unwrap();
      } catch (error) {
        console.error('Error deleting student:', error);
        alert('Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setConsultantFilter('');
    setActiveFilter('');
    setDebouncedSearchTerm('');
    setDebouncedConsultantFilter('');
    setDebouncedActiveFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const studentData = {
      ...formData,
      birth_date: formData.birth_date || null,
      consultant_id: formData.consultant_id ? parseInt(formData.consultant_id) : null,
      language_exam_date: formData.language_exam_date || null,
      is_active: formData.is_active
    };
    
    try {
      if (editingStudent) {
        await updateStudent({ id: editingStudent.id, obj: studentData }).unwrap();
      } else {
        await addStudent(studentData).unwrap();
      }
      
      setIsModalOpen(false);
      setEditingStudent(null);
    } catch (error) {
      console.error('Error saving student:', error);
      alert('Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
    }
  };

  // Get student statistics
  const totalStudents = students.length;
  const activeStudents = students.filter(s => s.is_active || s.isActive).length;
  const acceptedStudents = students.filter(s => (s.status_id || s.statusId) === 6).length; // Qəbul alıb
  const newStudents = students.filter(s => {
    const contactDateStr = s.first_contact_date || s.firstContactDate || s.created_at;
    if (!contactDateStr) return false;
    
    const contactDate = new Date(contactDateStr);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Invalid date check
    if (isNaN(contactDate.getTime())) return false;
    
    return contactDate > thirtyDaysAgo;
  }).length;



  if (studentsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tələbələr</h1>
          <p className="mt-1 text-sm text-gray-600">
            Bütün tələbələrin ümumi siyahısı və ətraflı məlumatları
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {students.length}
            </span>
            {(debouncedSearchTerm || debouncedConsultantFilter || debouncedActiveFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {filteredStudents.length}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <ExportButton 
            data={exportData}
            filename="telebeler"
            headers={exportHeaders}
            setIsExport={setIsExport}
          />
          { canAddStudent ? (<button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Yeni Tələbə
          </button>) : "" }
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">Ümumi Tələbələr</p>
              <p className="text-2xl font-bold">{totalStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Aktiv Tələbələr</p>
              <p className="text-2xl font-bold">{activeStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Qəbul Almış</p>
              <p className="text-2xl font-bold">{acceptedStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-orange-100 text-sm font-medium">Yeni (30 gün)</p>
              <p className="text-2xl font-bold">{newStudents}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ad, soyad, telefon və ya email ilə axtarış..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div>

        </div>
        <div>
          <select
            value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün konsultantlar</option>
            {consultants.filter(c => c.is_active || c.isActive).map(consultant => (
                              <option key={consultant.id} value={consultant.id}>
                  {consultant.admin?.first_name ? 
                    `${consultant.admin.first_name} ${consultant.admin.last_name}` : 
                    consultant.name || ''
                  }
                </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="1">Aktiv</option>
            <option value="0">Deaktiv</option>
          </select>
        </div>
        <div>
          <button
            onClick={handleClearFilters}
            disabled={!searchTerm && !consultantFilter && !activeFilter}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Filtri təmizlə
          </button>
        </div>
      </div>

      {/* Students table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Tələbə</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əlaqə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Təhsil
                  </th>
                                                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Konsultant
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Əməliyyatlar
                </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-4">
                          <span className="text-sm font-bold text-white">
                            {(student.first_name || student.firstName || '?')[0]}{(student.last_name || student.lastName || '?')[0]}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                            {(student.first_name || student.firstName || '')} {(student.last_name || student.lastName || '')}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {student.id} | {student.applications?.length || 0} müraciət
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center space-x-1 mb-1">
                          <PhoneIcon className="h-3 w-3 text-gray-400" />
                          <span>{student.phone}</span>
                        </div>
                        {student.email && (
                          <div className="flex items-center space-x-1">
                            <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                            <span className="truncate">{student.email}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                                              <div className="text-sm text-gray-900">
                          <div className="font-medium">{student.educationLevel?.name || student.education_level?.name || (typeof student.educationLevel === 'string' ? student.educationLevel : '') || (typeof student.education_level === 'string' ? student.education_level : '')}</div>
                        <div className="text-xs text-gray-500">{student.specialization?.name || (typeof student.specialization === 'string' ? student.specialization : '')}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(() => {
                          if (student.consultant?.admin?.first_name) {
                            return `${student.consultant.admin.first_name} ${student.consultant.admin.last_name}`;
                          } else if (student.consultant?.name) {
                            return student.consultant.name;
                          } else if (typeof student.consultant === 'string') {
                            return student.consultant;
                          } else if (student.consultant_id || student.consultantId) {
                            // Consultant ID var ama consultant objesi yox
                            const consultantId = student.consultant_id || student.consultantId;
                            const consultant = consultants.find(c => c.id === parseInt(consultantId));
                            if (consultant) {
                              return consultant.admin?.first_name ? 
                                `${consultant.admin.first_name} ${consultant.admin.last_name}` : 
                                consultant.name || 'Konsultant';
                            }
                            return `Konsultant ID: ${consultantId}`;
                          } else {
                            return 'Konsultant təyin edilməyib';
                          }
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.is_active || student.isActive ? 
                          'bg-green-100 text-green-800 border border-green-200' : 
                          'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {student.is_active || student.isActive ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {/* View button - only show if user has student.view permission */}
                        {canViewStudent && (
                          <button
                            onClick={() => handleView(student)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Edit button - only show if user has student.edit permission */}
                        <PermissionButton
                          permission="student.edit"
                          onClick={() => handleEdit(student)}
                          className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                          title="Redaktə et"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </PermissionButton>
                        
                        {/* Delete button - only show if user has student.delete permission */}
                        <PermissionButton
                          permission="student.delete"
                          onClick={() => handleDelete(student.id, `${student.first_name || student.firstName || ''} ${student.last_name || student.lastName || ''}`)}
                          className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                          title="Sil"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </PermissionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredStudents.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <UserIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Tələbə tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni tələbə əlavə edin.
              </p>
              <button
                onClick={handleAdd}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                İlk Tələbəni Əlavə Et
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filteredStudents.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredStudents.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* View Modal */}
      {isViewModalOpen && viewingStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {(viewingStudent.first_name || viewingStudent.firstName || '?')[0]}{(viewingStudent.last_name || viewingStudent.lastName || '?')[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {(viewingStudent.first_name || viewingStudent.firstName || '')} {(viewingStudent.last_name || viewingStudent.lastName || '')} - Ətraflı Məlumat
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      viewingStudent.statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
                      viewingStudent.statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                      viewingStudent.statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                      'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}>
                                              {viewingStudent.status?.name || (typeof viewingStudent.status === 'string' ? viewingStudent.status : '')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Tələbə Təfərrüatları
                  </button>
                  <button
                    onClick={() => setActiveTab('applications')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'applications'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Müraciətlər ({viewingStudent.applications?.length || 0})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-64">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Şəxsi Məlumatlar</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Ad Soyad</div>
                          <div className="text-sm font-semibold text-gray-900">{(viewingStudent.first_name || viewingStudent.firstName || '')} {(viewingStudent.last_name || viewingStudent.lastName || '')}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Telefon</div>
                          <div className="text-sm text-blue-600">
                            <a href={`tel:${viewingStudent.phone}`} className="hover:underline">
                              {viewingStudent.phone}
                            </a>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Email</div>
                          <div className="text-sm text-blue-600">
                            {viewingStudent.email ? (
                              <a href={`mailto:${viewingStudent.email}`} className="hover:underline">
                                {viewingStudent.email}
                              </a>
                            ) : 'Məlumat yoxdur'}
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Doğum Tarixi</div>
                          <div className="text-sm text-gray-900">{viewingStudent.birthDate || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100 md:col-span-2">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Ünvan</div>
                          <div className="text-sm text-gray-900">{viewingStudent.address || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Valideyn</div>
                          <div className="text-sm text-gray-900">{viewingStudent.parentName || 'Məlumat yoxdur'}</div>
                          {viewingStudent.parentPhone && (
                            <div className="text-xs text-blue-600 mt-1">
                              <a href={`tel:${viewingStudent.parentPhone}`} className="hover:underline">
                                {viewingStudent.parentPhone}
                              </a>
                            </div>
                          )}
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Konsultant</div>
                          <div className="text-sm font-semibold text-gray-900">
                    {(() => {
                      if (viewingStudent.consultant?.admin?.first_name) {
                        return `${viewingStudent.consultant.admin.first_name} ${viewingStudent.consultant.admin.last_name}`;
                      } else if (viewingStudent.consultant?.name) {
                        return viewingStudent.consultant.name;
                      } else if (typeof viewingStudent.consultant === 'string') {
                        return viewingStudent.consultant;
                      } else if (viewingStudent.consultant_id || viewingStudent.consultantId) {
                        const consultantId = viewingStudent.consultant_id || viewingStudent.consultantId;
                        const consultant = consultants.find(c => c.id === parseInt(consultantId));
                        if (consultant) {
                          return consultant.admin?.first_name ? 
                            `${consultant.admin.first_name} ${consultant.admin.last_name}` : 
                            consultant.name || 'Konsultant';
                        }
                        return `Konsultant ID: ${consultantId} (tapılmadı)`;
                      } else {
                        return 'Konsultant təyin edilməyib';
                      }
                    })()}
                  </div>
                        </div>
                      </div>
                    </div>

                    {/* Academic Information */}
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-green-600 rounded-lg flex items-center justify-center">
                          <AcademicCapIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-green-900">Akademik Məlumatlar</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Təhsil Səviyyəsi</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingStudent.educationLevel?.name || viewingStudent.education_level?.name || (typeof viewingStudent.educationLevel === 'string' ? viewingStudent.educationLevel : '') || (typeof viewingStudent.education_level === 'string' ? viewingStudent.education_level : '')}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">İxtisas</div>
                          <div className="text-sm text-gray-900">{viewingStudent.specialization?.name || (typeof viewingStudent.specialization === 'string' ? viewingStudent.specialization : '')}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Cari İl</div>
                          <div className="text-sm text-gray-900">{viewingStudent.currentYear}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">GPA</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingStudent.gpa}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100 md:col-span-2">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Cari Universitet</div>
                          <div className="text-sm text-gray-900">{viewingStudent.currentUniversity || 'Məlumat yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Dil İmtahanı</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingStudent.languageExam}</div>
                          {viewingStudent.languageExamDate && (
                            <div className="text-xs text-gray-500 mt-1">{viewingStudent.languageExamDate}</div>
                          )}
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Digər İmtahanlar</div>
                          <div className="text-sm text-gray-900">{viewingStudent.otherExams}</div>
                        </div>
                      </div>
                    </div>

                    {/* Application Preferences */}
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-purple-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-purple-900">Müraciət Tərcihləri</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Maraq Dairəsi Ölkələr</div>
                          <div className="text-sm text-gray-900">{viewingStudent.interestedCountries}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Maliyyə Proqnozu</div>
                          <div className="text-sm text-gray-900">{viewingStudent.financialForecast}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Təhsil Haqqı Aralığı</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingStudent.tuitionFeeRange}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Mənbə</div>
                          <div className="text-sm text-gray-900">{viewingStudent.source}</div>
                        </div>
                      </div>
                      {viewingStudent.generalNotes && (
                        <div className="mt-4">
                          <div className="bg-white p-4 rounded-lg border border-purple-100">
                            <div className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-2">Ümumi Qeydlər</div>
                            <div className="text-sm text-gray-900">{viewingStudent.generalNotes}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'applications' && (
                  <div className="space-y-3">
                    {viewingStudent.applications && viewingStudent.applications.length > 0 ? (
                      viewingStudent.applications.map((application) => (
                        <div key={application.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {application.university[0]}{application.program[0]}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900">
                                    {application.university}
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    application.status === 'Qəbul alıb' ? 'bg-green-100 text-green-800 border border-green-200' :
                                    application.status === 'Sənədlər tamamlandı' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    application.status === 'Cavab gözlənilir' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                    'bg-gray-100 text-gray-800 border border-gray-200'
                                  }`}>
                                    {application.status}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  <strong>Proqram:</strong> {application.program} |
                                  <strong> Müraciət tarixi:</strong> {application.applicationDate} |
                                  <strong> Son tarix:</strong> {application.deadline}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  <strong>Sənədlər:</strong> {application.requiredDocuments}
                                  {application.notes && (
                                    <span> | <strong>Qeyd:</strong> {application.notes}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                          <AcademicCapIcon className="h-6 w-6 text-orange-400" />
                        </div>
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
                        <p className="text-gray-500">Bu tələbə hələ heç bir universitetə müraciət etməyib</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEdit(viewingStudent);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                  Redaktə Et
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingStudent ? 'Tələbəni Redaktə Et' : 'Yeni Tələbə Əlavə Et'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingStudent ? 'Tələbə məlumatlarını yeniləyin' : 'Yeni tələbə məlumatlarını daxil edin'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Şəxsi Məlumatlar</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ad *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: Nigar"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Soyad *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: Rəhimova"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: +994502345678"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: nigar@gmail.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Doğum Tarixi
                      </label>
                      <input
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Konsultant *
                      </label>
                      <select
                        required
                        value={formData.consultant_id}
                        onChange={(e) => setFormData({ ...formData, consultant_id: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Konsultant seçin</option>
                        {consultants.filter(c => c.is_active || c.isActive).map(consultant => (
                          <option key={consultant.id} value={consultant.id}>
                      {consultant.admin?.first_name ? 
                        `${consultant.admin.first_name} ${consultant.admin.last_name}` : 
                        consultant.name || ''
                      }
                    </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ünvan
                      </label>
                      <textarea
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Məsələn: Bakı şəhəri, Nəsimi rayonu, Nizami küçəsi 45"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valideyn Adı
                      </label>
                      <input
                        type="text"
                        value={formData.parent_name}
                        onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: Rəhim Rəhimov"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valideyn Telefonu
                      </label>
                      <input
                        type="tel"
                        value={formData.parent_phone}
                        onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: +994502345677"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Academic Information Section */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <h4 className="text-lg font-semibold text-green-900 mb-4">Akademik Məlumatlar</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsil Səviyyəsi *
                      </label>
                      <select
                        required
                        value={formData.education_level}
                        onChange={(e) => setFormData({ ...formData, education_level: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Səviyyə seçin</option>
                        <option value="Bakalavr">Bakalavr</option>
                        <option value="Magistr">Magistr</option>
                        <option value="Doktorantura">Doktorantura</option>
                        <option value="Dil məktəbi">Dil məktəbi</option>
                        <option value="Orta məktəb">Orta məktəb</option>
                        <option value="Kurs/Kollec">Kurs/Kollec</option>
                        <option value="Transfer">Transfer</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İxtisaslaşma *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: İqtisadiyyat"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cari İl/Kurs
                      </label>
                      <input
                        type="text"
                        value={formData.current_year}
                        onChange={(e) => setFormData({ ...formData, current_year: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: 4"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GPA
                      </label>
                      <input
                        type="text"
                        value={formData.gpa}
                        onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: 87.5 / 100"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cari Universitet/Məktəb
                      </label>
                      <input
                        type="text"
                        value={formData.current_university}
                        onChange={(e) => setFormData({ ...formData, current_university: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: ADA Universiteti"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil İmtahanı
                      </label>
                      <input
                        type="text"
                        value={formData.language_exam}
                        onChange={(e) => setFormData({ ...formData, language_exam: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: IELTS 6.5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil İmtahan Tarixi
                      </label>
                      <input
                        type="date"
                        value={formData.language_exam_date}
                        onChange={(e) => setFormData({ ...formData, language_exam_date: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Digər İmtahanlar
                      </label>
                      <input
                        type="text"
                        value={formData.other_exams}
                        onChange={(e) => setFormData({ ...formData, other_exams: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Məsələn: GRE 320, SAT 1450"
                      />
                    </div>
                  </div>
                </div>

                {/* Status Section */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Status</h4>
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Tələbə aktiv statusdadır
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Aktiv tələbələr siyahıda görünür və əməliyyatlar həyata keçirilə bilər
                  </p>
                </div>


          
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingStudent ? 'Yenilə' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 